// ============================================
// Promotions Hooks - TanStack Query
// ============================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type {
  Promotion,
  CreatePromotionRequest,
  UpdatePromotionRequest,
  PromotionListParams,
  UUID,
} from '@conai/shared-types';
import { usePromotionsService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';
import { useI18n } from '../i18n';

/**
 * Hook to list promotions with pagination and filters
 */
export function usePromotions(params: PromotionListParams = {}) {
  const promotionsService = usePromotionsService();

  return useQuery({
    queryKey: queryKeys.promotions.list(params as Record<string, unknown>),
    queryFn: () => promotionsService.list(params),
  });
}

/**
 * Hook to list promotions for a specific store
 */
export function useStorePromotions(
  storeId: UUID | undefined,
  params: Omit<PromotionListParams, 'store'> = {}
) {
  const promotionsService = usePromotionsService();

  return useQuery({
    queryKey: queryKeys.promotions.list({ ...params, store: storeId }),
    queryFn: () => promotionsService.listByStore(storeId!, params),
    enabled: !!storeId,
  });
}

/**
 * Hook to list active promotions
 */
export function useActivePromotions(storeId?: UUID) {
  const promotionsService = usePromotionsService();

  return useQuery({
    queryKey: queryKeys.promotions.active(storeId),
    queryFn: () => promotionsService.listActive(storeId),
  });
}

/**
 * Hook to get a single promotion by ID
 */
export function usePromotion(id: string, options?: { enabled?: boolean }) {
  const promotionsService = usePromotionsService();

  return useQuery({
    queryKey: queryKeys.promotions.detail(id),
    queryFn: () => promotionsService.getById(id),
    enabled: options?.enabled !== false && !!id,
  });
}

/**
 * Hook to create a new promotion
 */
export function useCreatePromotion() {
  const promotionsService = usePromotionsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (data: CreatePromotionRequest) => promotionsService.create(data),
    onSuccess: (newPromotion) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.promotions.all });
      toast.success(t('toastPromotionCreated', { name: newPromotion.name }));
    },
  });
}

/**
 * Hook to update a promotion
 */
export function useUpdatePromotion() {
  const promotionsService = usePromotionsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePromotionRequest }) =>
      promotionsService.update(id, data),
    onSuccess: (updatedPromotion, variables) => {
      // Bug #132: KHÔNG ghi response của PUT thẳng vào cache detail.
      //
      // BE xác nhận 19/08/2026 `update()` đã trả đúng `PromotionSerializer` của
      // `retrieve()` (đủ `applicable_to`, `product_ids`/`category_ids`/`store_ids`,
      // `start_at`/`end_at`) và đọc lại từ DB sau khi ghi scope — nên về lý
      // `setQueryData` dùng được. Vẫn giữ cách này vì đắt hơn đúng MỘT GET, và
      // chỉ khi người dùng thực sự mở modal chi tiết. Đổi lại: màn chi tiết
      // không bao giờ phụ thuộc vào việc hai serializer còn khớp nhau — đúng bài
      // học đã ghi ở docs/BE_FOLLOWUP_18PLUS_2026-08-18.md §3.1, nơi một
      // response ghi thiếu field đã âm thầm xoá dữ liệu khỏi màn hình.
      //
      // removeQueries chứ không phải invalidateQueries: queryClient đặt
      // `refetchOnMount: false` toàn cục, nên invalidate một query đang inactive
      // chỉ đánh dấu stale — modal mở lại vẫn dựng từ cache cũ. Xoá hẳn thì lần
      // mở sau bắt buộc gọi GET detail.
      /**
       * Dọn theo `variables.id` — id ta VỪA gửi đi.
       *
       * BE xác nhận 18/09/2026 rằng `update()` luôn trả `id` (đọc lại từ DB rồi
       * serialize bằng đúng `PromotionSerializer` của `retrieve()`), nên
       * `updatedPromotion.id` cũng dùng được. Vẫn lấy từ `variables` vì nó đúng
       * kể cả khi response đổi hình dạng, và không tốn gì.
       *
       * (Ghi lại cho khỏi lần lại: bug #251 — "sửa Usage limit xong mở ra vẫn
       * số cũ" — KHÔNG phải lỗi cache. FE gửi sai tên field: `usage_limit` thay
       * vì `total_quantity`, DRF bỏ qua trong im lặng nên giá trị chưa từng
       * được lưu. Xem `PromotionsPage.handleSubmitForm`.)
       */
      queryClient.removeQueries({ queryKey: queryKeys.promotions.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.promotions.lists() });
      toast.success(t('toastPromotionUpdated', { name: updatedPromotion.name }));
    },
  });
}

/**
 * Hook to delete a promotion
 */
export function useDeletePromotion() {
  const promotionsService = usePromotionsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => promotionsService.delete(id),
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: queryKeys.promotions.detail(deletedId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.promotions.all });
      toast.success(t('toastPromotionDeleted'));
    },
  });
}

type PromotionResult = { id: string; status: string };

/**
 * Hook to activate a promotion (DRAFT -> ACTIVE)
 */
export function useActivatePromotion() {
  const promotionsService = usePromotionsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => promotionsService.activate(id) as Promise<PromotionResult>,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.promotions.detail(result.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.promotions.lists() });
      toast.success(t('toastPromotionActivated'));
    },
  });
}

/**
 * Hook to pause a promotion (ACTIVE -> PAUSED)
 */
export function usePausePromotion() {
  const promotionsService = usePromotionsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => promotionsService.pause(id) as Promise<PromotionResult>,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.promotions.detail(result.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.promotions.lists() });
      toast.success(t('toastPromotionPaused'));
    },
  });
}

/**
 * Hook to resume a paused promotion (PAUSED -> ACTIVE)
 */
export function useResumePromotion() {
  const promotionsService = usePromotionsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => promotionsService.resume(id) as Promise<PromotionResult>,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.promotions.detail(result.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.promotions.lists() });
      toast.success(t('toastPromotionResumed'));
    },
  });
}

/**
 * Hook to cancel a promotion
 */
export function useCancelPromotion() {
  const promotionsService = usePromotionsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => promotionsService.cancel(id) as Promise<PromotionResult>,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.promotions.detail(result.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.promotions.all });
      toast.success(t('toastPromotionCancelled'));
    },
  });
}

/**
 * Hook to get promotions expiring soon
 */
export function useExpiringSoonPromotions(days = 7, storeId?: UUID) {
  const promotionsService = usePromotionsService();

  return useQuery({
    queryKey: [...queryKeys.promotions.all, 'expiring-soon', days, storeId],
    queryFn: () => promotionsService.getExpiringSoon(days, storeId),
  });
}

/**
 * Hook to get flash sale promotions
 */
export function useFlashSales(storeId?: UUID) {
  const promotionsService = usePromotionsService();

  return useQuery({
    queryKey: [...queryKeys.promotions.all, 'flash-sales', storeId],
    queryFn: () => promotionsService.getFlashSales(storeId),
  });
}

/**
 * Calculate promotion stats from a list of promotions
 */
export function usePromotionStats(storeId?: UUID) {
  const { data, isLoading } = usePromotions({ store: storeId });
  const { data: activeData, isLoading: activeLoading } = useActivePromotions(storeId);

  const stats = {
    total: data?.count || 0,
    active: activeData?.count || 0,
    draft: 0,
    paused: 0,
    expired: 0,
    cancelled: 0,
  };

  // Calculate stats from results
  if (data?.results) {
    data.results.forEach((promo: Promotion) => {
      switch (promo.status) {
        case 'DRAFT':
          stats.draft++;
          break;
        case 'PAUSED':
          stats.paused++;
          break;
        case 'EXPIRED':
          stats.expired++;
          break;
        case 'CANCELLED':
          stats.cancelled++;
          break;
      }
    });
  }

  return {
    stats,
    isLoading: isLoading || activeLoading,
  };
}
